/**
 * PrepInspectScreen.tsx
 * Phase 3 — Écran d'inspection libre (sans contrat).
 *
 * L'agent saisit une plaque d'immatriculation, puis lance l'inspection
 * comme d'habitude (photos, fuel, dommages, etc.).
 * À la fin, la préparation est publiée vers Supabase pour être
 * récupérée par un autre agent au comptoir.
 *
 * @version 1.0.0
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useAppStore } from '../store/useAppStore'
import { ArrowLeft, Search, Car, Loader2, Wrench, X } from 'lucide-react'
import Card   from '../components/ui/Card'
import Button from '../components/ui/Button'
import api    from '../services/api'

// ── Types ──────────────────────────────────────────────────────────────────
interface PlateMatch {
  id:           string | number
  plate:        string
  vehicleName?: string
  clientName?:  string
}

export default function PrepInspectScreen() {
  const setStep          = useAppStore(s => s.setStep)
  const startInspection  = useAppStore(s => s.startInspection)
  const contracts        = useAppStore(s => s.contracts)

  const [plate, setPlate]             = useState('')
  const [vehicleName, setVehicleName] = useState('')
  const [suggestions, setSuggestions]  = useState<PlateMatch[]>([])
  const [searching, setSearching]      = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Recherche dynamique dans les contrats locaux ─────────────────────
  const searchPlate = useCallback((query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const upper = query.toUpperCase().replace(/[^A-Z0-9]/g, '')
    const matches = (contracts as any[])
      .filter((c: any) => {
        const p = (c.plate || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
        return p.includes(upper)
      })
      .slice(0, 8)
      .map((c: any) => ({
        id:          c.id,
        plate:       c.plate,
        vehicleName: c.vehicleName || c.vehicle_name || '',
        clientName:  c.clientName || c.client_name || '',
      }))

    setSuggestions(matches)
    setShowSuggestions(matches.length > 0)
  }, [contracts])

  // ── Input avec debounce ────────────────────────────────────────────
  const handlePlateInput = (value: string) => {
    const formatted = value.toUpperCase()
    setPlate(formatted)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchPlate(formatted), 200)
  }

  // ── Sélection d'une suggestion ──────────────────────────────────────
  const handleSelectSuggestion = (match: PlateMatch) => {
    setPlate(match.plate)
    setVehicleName(match.vehicleName || '')
    setShowSuggestions(false)
    setSuggestions([])
  }

  // ── Lancer l'inspection préparatoire ────────────────────────────────
  const handleStart = (type: 'depart' | 'retour') => {
    if (!plate.trim()) return

    // Créer un pseudo-contrat pour le flux existant
    const pseudoContract = {
      id:           `prep-${Date.now()}`,
      rawId:        `prep-${Date.now()}`,
      plate:        plate.trim(),
      vehicleName:  vehicleName || plate.trim(),
      clientName:   'Préparation libre',
      clientEmail:  '',
      agencyEmail:  '',
      typeDocument: 'PREP',
      _isPrep:      true,   // Flag pour identifier une préparation
    }

    startInspection(pseudoContract, type)
  }

  // ── Cleanup debounce ──────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const isValid = plate.trim().length >= 4

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-4 pt-5 pb-5">
        <button
          onClick={() => setStep('contracts')}
          className="text-white/70 hover:text-white mb-3 flex items-center gap-1 text-sm"
        >
          <ArrowLeft size={16}/> Retour
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Wrench size={22} className="text-white"/>
          </div>
          <div>
            <h1 className="text-white font-black text-xl">Inspection libre</h1>
            <p className="text-white/70 text-sm">Sans contrat — préparation au garage</p>
          </div>
        </div>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-5 max-w-lg mx-auto w-full">

        {/* Saisie plaque */}
        <Card className="p-5">
          <label className="text-sm font-bold text-gray-700 mb-2 block">
            Plaque d'immatriculation
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search size={18}/>
            </div>
            <input
              type="text"
              value={plate}
              onChange={e => handlePlateInput(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true) }}
              placeholder="AA-123-BB ou juste 123"
              className="w-full pl-10 pr-10 py-3 text-lg font-mono font-bold tracking-wider text-center
                         border-2 border-gray-200 rounded-xl
                         focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none
                         transition-all uppercase"
              autoComplete="off"
              autoCapitalize="characters"
            />
            {plate && (
              <button
                onClick={() => { setPlate(''); setVehicleName(''); setSuggestions([]); setShowSuggestions(false) }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18}/>
              </button>
            )}
          </div>

          {/* Suggestions de plaques */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
              {suggestions.map((match) => (
                <button
                  key={String(match.id)}
                  onClick={() => handleSelectSuggestion(match)}
                  className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center gap-3 border-b border-gray-100 last:border-0 transition-colors"
                >
                  <Car size={16} className="text-gray-400 flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <span className="font-mono font-bold text-sm text-gray-800">{match.plate}</span>
                    {match.vehicleName && (
                      <span className="text-xs text-gray-500 ml-2">{match.vehicleName}</span>
                    )}
                    {match.clientName && (
                      <p className="text-xs text-gray-400 truncate">{match.clientName}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Nom véhicule (optionnel) */}
          {isValid && (
            <div className="mt-3">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">
                Véhicule (optionnel)
              </label>
              <input
                type="text"
                value={vehicleName}
                onChange={e => setVehicleName(e.target.value)}
                placeholder="Ex: RENAULT MASTER L2H2"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg
                           focus:border-amber-400 outline-none transition-colors"
              />
            </div>
          )}
        </Card>

        {/* Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-700 text-center">
            🔧 L'inspection sera publiée comme préparation.
            Un agent au comptoir pourra la lier à un contrat client pour finaliser.
          </p>
        </div>

        {/* Boutons Départ / Retour */}
        {isValid && (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => handleStart('depart')}
              className="w-full"
            >
              <Card className="p-6 cursor-pointer hover:shadow-lg transition-all border-2 hover:border-green-400 active:scale-[0.98]">
                <div className="text-center">
                  <div className="text-4xl mb-2">🚗</div>
                  <h2 className="text-xl font-black text-green-600">DÉPART</h2>
                  <p className="text-gray-500 text-xs mt-1">Préparation départ — {plate}</p>
                </div>
              </Card>
            </button>

            <button
              onClick={() => handleStart('retour')}
              className="w-full"
            >
              <Card className="p-6 cursor-pointer hover:shadow-lg transition-all border-2 hover:border-red-400 active:scale-[0.98]">
                <div className="text-center">
                  <div className="text-4xl mb-2">🏁</div>
                  <h2 className="text-xl font-black text-red-600">RETOUR</h2>
                  <p className="text-gray-500 text-xs mt-1">Préparation retour — {plate}</p>
                </div>
              </Card>
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
